import React, { useRef } from 'react';
import { GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const LogoExtractor = () => {
  const svgRef = useRef(null);

  const downloadSVG = () => {
    const svgElement = svgRef.current.querySelector('svg');
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);
    const downloadLink = document.createElement('a');
    downloadLink.href = svgUrl;
    downloadLink.download = 'lyceum-logo.svg';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const downloadPNG = async (size, transparent = false) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    canvas.width = size;
    canvas.height = size;
    
    // Create SVG with embedded amber color (#b45309 is amber-700)
    const svgString = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="#b45309" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z"/>
        <path d="M22 10v6"/>
        <path d="M6 12.5V16a6 3 0 0 0 12 0v-3.5"/>
      </svg>
    `;
    
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    
    img.onload = () => {
      // Fill background if not transparent
      if (!transparent) {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, size, size);
      }
      
      // Draw the SVG
      ctx.drawImage(img, 0, 0, size, size);
      
      canvas.toBlob((blob) => {
        const pngUrl = URL.createObjectURL(blob);
        const downloadLink = document.createElement('a');
        downloadLink.href = pngUrl;
        const bgType = transparent ? 'transparent' : 'white';
        downloadLink.download = `lyceum-amber-${bgType}-${size}x${size}.png`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(url);
        URL.revokeObjectURL(pngUrl);
      });
    };
    
    img.src = url;
  };

  const copySVGCode = () => {
    const svgElement = svgRef.current.querySelector('svg');
    const svgData = new XMLSerializer().serializeToString(svgElement);
    navigator.clipboard.writeText(svgData);
    alert('SVG code copied to clipboard!');
  };

  return (
    <div className="min-h-screen theme-bg p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-amber-900 mb-8 text-center">
          Lyceum Hat Logo Extractor
        </h1>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Preview Section */}
          <Card className="card-glow p-8 bg-white shadow-lg">
            <h2 className="text-xl font-semibold text-amber-900 mb-4">Preview</h2>
            
            {/* White Background */}
            <div className="mb-6 p-8 bg-white border-2 border-gray-200 rounded-lg flex items-center justify-center">
              <div ref={svgRef}>
                <GraduationCap className="w-32 h-32 text-amber-700" strokeWidth={1.5} />
              </div>
            </div>

            {/* Transparent Background */}
            <div className="mb-6 p-8 bg-gray-100 rounded-lg flex items-center justify-center" style={{
              backgroundImage: 'repeating-linear-gradient(45deg, #f0f0f0 0px, #f0f0f0 10px, #e0e0e0 10px, #e0e0e0 20px)'
            }}>
              <GraduationCap className="w-32 h-32 text-amber-700" strokeWidth={1.5} />
            </div>

            {/* Different Sizes */}
            <div className="flex items-end justify-around gap-4 p-4 bg-amber-50 rounded-lg">
              <GraduationCap className="w-12 h-12 text-amber-700" strokeWidth={1.5} />
              <GraduationCap className="w-16 h-16 text-amber-700" strokeWidth={1.5} />
              <GraduationCap className="w-24 h-24 text-amber-700" strokeWidth={1.5} />
            </div>
          </Card>

          {/* Download Options */}
          <Card className="card-glow p-8 bg-white shadow-lg">
            <h2 className="text-xl font-semibold text-amber-900 mb-4">Download Options</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-amber-800 mb-2">Vector Format (SVG)</h3>
                <div className="space-y-2">
                  <Button 
                    onClick={downloadSVG}
                    className="w-full bg-amber-600 hover:bg-amber-700"
                  >
                    Download SVG
                  </Button>
                  <Button 
                    onClick={copySVGCode}
                    variant="outline"
                    className="w-full border-amber-600 text-amber-700 hover:bg-amber-50"
                  >
                    Copy SVG Code
                  </Button>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-amber-800 mb-2">PNG - White Background</h3>
                <div className="space-y-2">
                  <Button 
                    onClick={() => downloadPNG(512, false)}
                    variant="outline"
                    className="w-full border-amber-600 text-amber-700 hover:bg-amber-50"
                  >
                    Download PNG (512x512)
                  </Button>
                  <Button 
                    onClick={() => downloadPNG(1024, false)}
                    variant="outline"
                    className="w-full border-amber-600 text-amber-700 hover:bg-amber-50"
                  >
                    Download PNG (1024x1024)
                  </Button>
                  <Button 
                    onClick={() => downloadPNG(2048, false)}
                    variant="outline"
                    className="w-full border-amber-600 text-amber-700 hover:bg-amber-50"
                  >
                    Download PNG (2048x2048)
                  </Button>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-amber-800 mb-2">PNG - Transparent</h3>
                <div className="space-y-2">
                  <Button 
                    onClick={() => downloadPNG(512, true)}
                    variant="outline"
                    className="w-full border-amber-600 text-amber-700 hover:bg-amber-50"
                  >
                    Download PNG (512x512)
                  </Button>
                  <Button 
                    onClick={() => downloadPNG(1024, true)}
                    variant="outline"
                    className="w-full border-amber-600 text-amber-700 hover:bg-amber-50"
                  >
                    Download PNG (1024x1024)
                  </Button>
                  <Button 
                    onClick={() => downloadPNG(2048, true)}
                    variant="outline"
                    className="w-full border-amber-600 text-amber-700 hover:bg-amber-50"
                  >
                    Download PNG (2048x2048)
                  </Button>
                </div>
              </div>

              <div className="pt-4 border-t border-amber-200">
                <h3 className="font-medium text-amber-800 mb-2">Screenshot Method</h3>
                <p className="text-sm text-gray-600">
                  Right-click on any preview above and select "Save image as..." or take a screenshot
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Color Variations */}
        <Card className="card-glow mt-8 p-8 bg-white shadow-lg">
          <h2 className="text-xl font-semibold text-amber-900 mb-4">Color Variations</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="p-6 bg-white border-2 border-gray-200 rounded-lg mb-2">
                <GraduationCap className="w-20 h-20 text-amber-700 mx-auto" strokeWidth={1.5} />
              </div>
              <p className="text-sm text-gray-600">Amber (Current)</p>
            </div>
            <div className="text-center">
              <div className="p-6 bg-white border-2 border-gray-200 rounded-lg mb-2">
                <GraduationCap className="w-20 h-20 text-black mx-auto" strokeWidth={1.5} />
              </div>
              <p className="text-sm text-gray-600">Black</p>
            </div>
            <div className="text-center">
              <div className="p-6 bg-gray-800 border-2 border-gray-200 rounded-lg mb-2">
                <GraduationCap className="w-20 h-20 text-white mx-auto" strokeWidth={1.5} />
              </div>
              <p className="text-sm text-gray-600">White</p>
            </div>
            <div className="text-center">
              <div className="p-6 bg-white border-2 border-gray-200 rounded-lg mb-2">
                <GraduationCap className="w-20 h-20 text-blue-600 mx-auto" strokeWidth={1.5} />
              </div>
              <p className="text-sm text-gray-600">Blue</p>
            </div>
          </div>
        </Card>

        <div className="mt-8 text-center">
          <Button 
            onClick={() => window.location.href = '/'}
            variant="outline"
            className="border-amber-600 text-amber-700 hover:bg-amber-50"
          >
            Back to Lyceum
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LogoExtractor;
